import { useLayoutEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2, ChevronDown, Sparkles, XCircle } from "lucide-react";
import { computeDocAiValue, setProjectBranch } from "../api";
import type { DocTemplateCheck, DocTemplateField } from "../types";

// Wysokości pola wartości ręcznej (px). Pola AI startują z zapasem — opis prac ułożony
// „na żywo" to zwykle kilka–kilkanaście linijek, a nie jeden wiersz.
const AI_MIN_H = 108;
const PLAIN_MIN_H = 28;
// Do tej wysokości pole rośnie samo; wyżej — tylko ręcznie uchwytem (CSS max-height
// blokowałoby też przeciąganie, więc limit jest wyłącznie po stronie auto-dopasowania).
const AUTO_MAX_H = 320;

/** Pole wartości ręcznej: rośnie z treścią (długie opisy AI mieszczą się bez scrolla)
 *  i zostaje rozciągalne uchwytem. Po ręcznym rozciągnięciu auto-dopasowanie milknie,
 *  żeby kolejne „Policz AI" nie cofało ustawionej wysokości. */
function ValueTextarea({
  value, placeholder, big, onChange,
}: {
  value: string;
  placeholder: string;
  big: boolean;
  onChange: (value: string) => void;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const autoH = useRef(0); // ostatnia wysokość ustawiona automatycznie
  const manual = useRef(false); // użytkownik przeciągnął uchwyt — nie ruszamy wysokości
  const minH = big ? AI_MIN_H : PLAIN_MIN_H;

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || manual.current) return;
    el.style.height = "auto";
    const borders = el.offsetHeight - el.clientHeight; // box-sizing: border-box
    const h = Math.min(Math.max(el.scrollHeight + borders, minH), AUTO_MAX_H);
    el.style.height = `${h}px`;
    autoH.current = h;
  }, [value, minH]);

  return (
    <textarea
      ref={ref}
      className="input-base w-full text-[11px] py-1 mt-0.5 resize-y"
      rows={1}
      value={value}
      placeholder={placeholder}
      title="Pole rośnie z treścią — możesz je też rozciągnąć uchwytem w prawym dolnym rogu"
      onChange={(e) => onChange(e.target.value)}
      // Wysokość inna niż ta z auto-dopasowania = ktoś pociągnął uchwyt.
      onMouseUp={(e) => {
        if (e.currentTarget.offsetHeight !== autoH.current) manual.current = true;
      }}
    />
  );
}

// Pre-generation breakdown of a template's variables in the chosen context:
//  • which will be filled automatically (green, + where each comes from),
//  • which have NO data (amber) — the user can type a value by hand (multiline) or
//    choose how empty fields render (blank / leave the #name# placeholder),
//  • which the context cannot supply AT ALL (red) — these block generation; the
//    template must drop them or be generated from a matching context.
// Manual overrides win over the resolved value for ANY field (filled or empty).
// Per-field extras: „zapisz na stałe do bazy" (writable) and „Policz AI teraz" (ai).
export function TemplateFieldsPanel({
  fields,
  overrides,
  onOverride,
  emptyMode,
  onEmptyMode,
  persist,
  onPersist,
  contextKind,
  contextId,
  branchSetup,
}: {
  fields: DocTemplateField[];
  overrides: Record<string, string>;
  onOverride: (name: string, value: string) => void;
  emptyMode: "blank" | "marker";
  onEmptyMode: (mode: "blank" | "marker") => void;
  // nazwy pól, których ręczne wartości zapiszą się też NA STAŁE do bazy
  persist?: string[];
  onPersist?: (name: string, on: boolean) => void;
  // kontekst generowania — potrzebny tylko do liczenia zmiennych AI „na żywo"
  contextKind?: string;
  contextId?: string;
  // szablon używa pól oddziału klienta, a projekt go nie ma → inline setter
  branchSetup?: DocTemplateCheck["client_branch_setup"];
}) {
  const qc = useQueryClient();
  const [showFilled, setShowFilled] = useState(false);
  const [aiBusy, setAiBusy] = useState<string | null>(null);
  // nazwa pola → komunikat błędu z backendu (np. „Brak elementów trasy na działkach…")
  const [aiError, setAiError] = useState<{ name: string; message: string } | null>(null);
  const [branchId, setBranchId] = useState("");
  const [branchBusy, setBranchBusy] = useState(false);
  const [branchError, setBranchError] = useState(false);
  const filled = fields.filter((f) => f.status === "filled");
  const empties = fields.filter((f) => f.status === "empty");
  const missing = fields.filter((f) => f.status === "missing");
  // `#wpisz_…#` — pola ręczne: zostają w dokumencie i czekają na edytor w przeglądarce.
  const manual = fields.filter((f) => f.status === "manual");
  if (fields.length === 0) return null;

  const saveBranch = async () => {
    if (!branchSetup?.connection_id || !branchId) return;
    setBranchBusy(true);
    setBranchError(false);
    try {
      await setProjectBranch(branchSetup.connection_id, branchId);
      // podgląd pól i dane projektu liczą się od nowa z ustawionym oddziałem
      qc.invalidateQueries({ queryKey: ["pp", "doc-template-check"] });
      qc.invalidateQueries({ queryKey: ["pp"] });
    } catch {
      setBranchError(true);
    } finally {
      setBranchBusy(false);
    }
  };

  const computeAi = async (name: string) => {
    if (!contextKind || !contextId) return;
    setAiBusy(name);
    setAiError(null);
    try {
      const r = await computeDocAiValue(name, contextKind, contextId);
      onOverride(name, r.value);
    } catch (err) {
      const detail = (err as { response?: { data?: { errors?: { detail?: unknown } } } })
        ?.response?.data?.errors?.detail;
      setAiError({
        name,
        message: typeof detail === "string" && detail
          ? detail
          : "Nie udało się policzyć — spróbuj ponownie.",
      });
    } finally {
      setAiBusy(null);
    }
  };

  const overrideBox = (f: DocTemplateField) => (
    <>
      <ValueTextarea
        value={overrides[f.name] ?? ""}
        placeholder="wpisz wartość ręcznie (opcjonalnie)"
        big={!!f.ai}
        onChange={(v) => onOverride(f.name, v)}
      />
      {(f.writable || (f.ai && contextKind && contextId)) && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5">
          {f.ai && contextKind && contextId && (
            <button
              type="button"
              className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wide text-[var(--color-primary)] disabled:opacity-50"
              disabled={aiBusy !== null}
              onClick={() => computeAi(f.name)}
              title="Ułóż wartość AI od zera z aktualnych danych projektu — bez czytania zapisanej wartości i bez zapisu do bazy"
            >
              <Sparkles size={11} />
              {aiBusy === f.name ? "liczę…" : "Policz AI teraz (live)"}
            </button>
          )}
          {aiError?.name === f.name && (
            <span className="text-[10px] text-red-600">{aiError.message}</span>
          )}
          {f.writable && onPersist && (
            <label
              className="flex items-center gap-1 text-[10px] text-[var(--color-text-muted)] cursor-pointer"
              title={`Wpisana wartość zapisze się też na stałe: ${f.write_target ?? ""}`}
            >
              <input
                type="checkbox"
                checked={(persist ?? []).includes(f.name)}
                disabled={!(overrides[f.name] ?? "").trim()}
                onChange={(e) => onPersist(f.name, e.target.checked)}
              />
              zapisz na stałe do bazy{f.write_target ? ` (${f.write_target})` : ""}
            </label>
          )}
        </div>
      )}
    </>
  );

  return (
    <div className="space-y-2">
      {/* szablon używa pól oddziału klienta, a projekt go nie ma — ustaw w miejscu */}
      {branchSetup?.needed && (
        <div className="border border-amber-300 dark:border-amber-500/40 bg-amber-50/60 dark:bg-amber-500/10 p-2 space-y-1.5">
          <p className="flex items-center gap-1.5 text-amber-700 dark:text-amber-400 font-medium text-xs">
            <AlertTriangle size={13} /> Projekt nie ma oddziału klienta ({branchSetup.client_name}) —
            pola <span className="font-mono">#klient_oddzial_*#</span> wyjdą puste.
          </p>
          {branchSetup.branches.length > 0 ? (
            <div className="flex flex-wrap items-center gap-2">
              <select
                className="input-base text-xs"
                value={branchId}
                onChange={(e) => setBranchId(e.target.value)}
              >
                <option value="">— wybierz oddział —</option>
                {branchSetup.branches.map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
              <button
                type="button"
                className="text-xs font-mono uppercase tracking-wider px-2 py-1 border border-amber-600 text-amber-700 dark:text-amber-400 disabled:opacity-50"
                disabled={!branchId || branchBusy}
                onClick={saveBranch}
              >
                {branchBusy ? "zapisuję…" : "Ustaw oddział projektu"}
              </button>
              {branchError && (
                <span className="text-[11px] text-red-600">Nie udało się ustawić oddziału.</span>
              )}
            </div>
          ) : (
            <p className="text-[11px] text-amber-700/80 dark:text-amber-400/80">
              Klient nie ma zdefiniowanych oddziałów — dodaj oddział w rejestrze klientów,
              wtedy ustawisz go tutaj.
            </p>
          )}
        </div>
      )}

      {/* missing — the context cannot supply these at all; generation is blocked */}
      {missing.length > 0 && (
        <div className="border border-red-300 dark:border-red-500/40 bg-red-50/60 dark:bg-red-500/10 p-2 space-y-1.5">
          <p className="flex items-center gap-1.5 text-red-700 dark:text-red-400 font-medium text-xs">
            <XCircle size={13} /> Zmienne niedostępne w tym kontekście ({missing.length}) — blokują generowanie:
          </p>
          <div className="space-y-1 max-h-40 overflow-y-auto">
            {missing.map((f) => (
              <div key={f.name} className="flex items-baseline justify-between gap-2 border-t border-red-200 dark:border-red-500/20 pt-1">
                <span className="font-mono text-[11px] text-[var(--color-text-primary)]">#{f.name}#</span>
                <span className="text-[10px] text-[var(--color-text-muted)] text-right">{f.source || "—"}</span>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-red-700/80 dark:text-red-400/80">
            Ten kontekst nie zna tych zmiennych — usuń je z pliku szablonu (lub zmień rodzaj
            szablonu na pasujący do tego okna) i wgraj plik ponownie w Generacji dokumentów.
          </p>
        </div>
      )}

      {/* manual — uzupełniane po generacji, w edytorze (nie blokują generowania) */}
      {manual.length > 0 && (
        <div className="border border-blue-300 dark:border-blue-500/40 bg-blue-50/60 dark:bg-blue-500/10 p-2 space-y-1.5">
          <p className="flex items-center gap-1.5 text-blue-800 dark:text-blue-300 font-medium text-xs">
            <AlertTriangle size={13} /> Pola do ręcznego uzupełnienia w edytorze ({manual.length}):
          </p>
          <div className="flex flex-wrap gap-x-3 gap-y-0.5">
            {manual.map((f) => (
              <span key={f.name} className="font-mono text-[11px] text-[var(--color-text-primary)]">#{f.name}#</span>
            ))}
          </div>
          <p className="text-[11px] text-blue-800/80 dark:text-blue-300/80">
            Zostaną w wygenerowanym piśmie jako znaczniki — po generacji otworzy się edytor
            w przeglądarce, w którym je wpiszesz. Pakiet nie skompiluje się, dopóki którykolwiek
            z nich zostaje w dokumencie.
          </p>
        </div>
      )}

      {/* filled automatically */}
      {filled.length > 0 && (
        <div className="border border-green-300 dark:border-green-500/40 bg-green-50/50 dark:bg-green-500/10">
          <button
            type="button"
            onClick={() => setShowFilled((v) => !v)}
            className="w-full flex items-center gap-1.5 px-2 py-1.5 text-green-700 dark:text-green-400 font-medium text-xs"
          >
            <CheckCircle2 size={13} /> Zostaną wypełnione automatycznie ({filled.length})
            <ChevronDown size={14} className={`ml-auto transition-transform ${showFilled ? "rotate-180" : ""}`} />
          </button>
          {showFilled && (
            <div className="px-2 pb-2 space-y-1.5 max-h-[45vh] overflow-y-auto">
              {filled.map((f) => (
                <div key={f.name} className="border-t border-green-200 dark:border-green-500/20 pt-1.5">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="font-mono text-[11px] text-[var(--color-text-primary)]">#{f.name}#</span>
                    <span className="text-[10px] text-[var(--color-text-muted)] text-right">{f.source || "—"}</span>
                  </div>
                  {overrideBox(f)}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* empty — no data */}
      {empties.length > 0 && (
        <div className="border border-amber-300 dark:border-amber-500/40 bg-amber-50/60 dark:bg-amber-500/10 p-2 space-y-2">
          <p className="flex items-center gap-1.5 text-amber-700 dark:text-amber-400 font-medium text-xs">
            <AlertTriangle size={13} /> Pola bez danych ({empties.length}) — uzupełnij ręcznie lub wybierz, jak je potraktować:
          </p>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px]">
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input type="radio" checked={emptyMode === "blank"} onChange={() => onEmptyMode("blank")} />
              Zostaw puste
            </label>
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input type="radio" checked={emptyMode === "marker"} onChange={() => onEmptyMode("marker")} />
              Oznacz <span className="font-mono">#nazwa#</span> do ręcznego wypełnienia
            </label>
          </div>
          <div className="space-y-1.5 max-h-[55vh] overflow-y-auto">
            {empties.map((f) => (
              <div key={f.name} className="border-t border-amber-200 dark:border-amber-500/20 pt-1.5">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="font-mono text-[11px] text-[var(--color-text-primary)]">#{f.name}#</span>
                  <span className="text-[10px] text-[var(--color-text-muted)] text-right">
                    Gdzie ustawić: {f.source || "—"}
                  </span>
                </div>
                {overrideBox(f)}
              </div>
            ))}
          </div>
          <p className="text-[11px] text-amber-700/80 dark:text-amber-400/80">
            Dokument i tak się wygeneruje. Wpisane tu wartości trafiają wprost do dokumentu (nadpisują dane
            z systemu); zaznaczenie „zapisz na stałe" utrwala wartość też w bazie.
          </p>
        </div>
      )}
    </div>
  );
}
